import '../input/select-input';

import{ html, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

import { AddressData, CartData, cartStore } from '../../_stores/cart-store';
import { SelectInput } from '../input/select-input';
import { CheckoutForm } from './checkout-form';

type AddressType = 'shipping' | 'billing';

export interface StateData {
    name: string;
    code: string;
}

export interface CountryData {
    name: string;
    code: string;
    available: boolean;
    states: StateData[]
}

@customElement('address-form')
export class AddressForm extends CheckoutForm {

    @property({ type: String })
    addressType: AddressType = 'shipping';

    @property({ type: Boolean, attribute: 'has-button' })
    hasButton: boolean = false;

    @state()
    _countryList: CountryData[] = [];

    @state()
    _countryCode?: string = null;

    // Lifecycle Hooks

    async firstUpdated() {

        // add classes
        this.classList.add('Form', 'Form--address');

		// handle slots
        const slot: HTMLElement | null = this.querySelector('[slot="crsf"]');
        if (slot) {
            this._crsf = slot.innerHTML;
            slot.remove();
        }

        // country data
        const response = await fetch('/data/countries');
        const countryList = await response.json();
        this._countryList = countryList;

        // country data
        let cartData = cartStore.data.get();
        if (!cartData) cartData = await cartStore.getCart();
        this._countryCode = cartData?.[`${ this.addressType }Address`]?.countryCode;

    }

    // Render

    render() {
        const cartData = cartStore.data.get();
        const addressType = this.addressType;
        const address: AddressData | null = cartData?.[`${ this.addressType }Address`];
        return html`
            <form 
                autocomplete="on" 
                method="post"
                @change="${ this._validate }"
				action="/checkout/shipping">
                ${ unsafeHTML(this._crsf) }

                <section class="Form-body">
					<h4>${ addressType.charAt(0).toUpperCase() + addressType.slice(1) } Address</h4>
                    <div class="Form-grid">
                        ${ this.emailInputTemplate(cartData) }
                        ${ this.nameInputTemplate(cartData, addressType, address) }
                        ${ this.addressLine1InputTemplate(cartData, addressType, address) }
                        ${ this.addressLine2InputTemplate(cartData, addressType, address) }
                        ${ this.cityInputTemplate(cartData, addressType, address) }
						${ this.stateInputTemplate(cartData, addressType, address) }
                        ${ this.zipCodeTemplate(cartData, addressType, address) }
                        ${ this.countryInputTemplate(cartData, addressType, address) }
						${ (addressType == 'shipping')
                            ? this.hiddenInputTemplate('shippingMethodHandle', cartData?.shippingMethodHandle) 
                            : '' }						
					</div>
                </section>

				<footer class="Form-footer">
					<div class="Form-block" col-span="2">
						<button-input
							type="link"
							.disabled="${ !cartData?.shippingAddress }"
							name="submit"
							href="/checkout/shipping">
							Choose a Shipping Method
						</button-input>
					</div>
				</footer>

            </form>
        `;
    }

    createRenderRoot() { 
        return this;
    }

    // Templates

    emailInputTemplate(cartData: CartData): TemplateResult {
        const error = this._errors['email'];
        return html`
            <div class="Form-block" col-span="2">
                <label for="email">
                    Email Address
                </label>
                <text-input 
                    name="email" 
                    autocomplete="email" 
                    value="${ cartData?.email ?? '' }"
                    .required="${ true }"
                ></text-input>
                ${ (error) ? this.alertTemplate(error) : '' }
            </div>
        `;
    }

    nameInputTemplate(cartData: CartData, addressType: AddressType, address?: AddressData): TemplateResult {
        const error = this._errors[`${ addressType }Address.fullName`];
        return html`
            <div class="Form-block" col-span="2">
                <label for="${ addressType }Address[fullName]">
                    Full Name
                </label>
                <text-input 
                    name="${ addressType }Address[fullName]" 
                    value="${ address?.fullName ?? '' }" 
                    autocomplete="name" 
                    .required="${ true }"
                ></text-input>
                ${ (error) ? this.alertTemplate(error) : '' }
            </div>
        `;
    }

    addressLine1InputTemplate(cartData: CartData, addressType: AddressType, address?: AddressData): TemplateResult {
        const error = this._errors[`${ addressType }.addressLine1`];
        return html`
            <div class="Form-block" col-span="2">
                <label for="${ addressType }Address[addressLine1]">
                    Address Line 1
                </label>
                <text-input 
                    name="${ addressType }Address[addressLine1]" 
                    value="${ address?.addressLine1 ?? '' }" 
                    autocomplete="address-line1 " 
                    .required="${ true }"
                ></text-input>
                ${ (error) ? this.alertTemplate(error) : '' }
            </div>
        `;
    }

    addressLine2InputTemplate(cartData: CartData, addressType: AddressType, address?: AddressData): TemplateResult {
        const error = this._errors[`${ addressType }Address.addressLine2`];
        return html`
            <div class="Form-block" col-span="2">
                <label for="${ addressType }Address[addressLine2]">
                    Address Line 2 (Optional)
                </label>
                <text-input 
                    name="${ addressType }Address[addressLine2]" 
                    value="${ address?.addressLine2 ?? '' }" 
                    autocomplete="address-line2" 
                ></text-input>
                ${ (error) ? this.alertTemplate(error) : '' }
            </div>
        `;
    }

    cityInputTemplate(cartData: CartData, addressType: AddressType, address?: AddressData): TemplateResult {
        const error = this._errors[`${ addressType }Address.locality`];
        return html`
            <div class="Form-block" col-span="1">
                <label for="${ addressType }Address[locality]">
                    City
                </label>
                <text-input 
                    name="${ addressType }Address[locality]" 
                    value="${ address?.locality ?? '' }" 
                    autocomplete="address-level2" 
                    .required="${ true }"
                ></text-input>
                ${ (error) ? this.alertTemplate(error) : '' }
            </div>
        `;
    }

    zipCodeTemplate(cartData: CartData, addressType: AddressType, address?: AddressData): TemplateResult {
        const error = this._errors[`${ addressType }Address.postalCode`];
        return html`
            <div class="Form-block" col-span="1">
                <label for="${ addressType }Address[postalCode]">
                    Zip or Postal Code
                </label>
                <text-input 
                    name="${ addressType }Address[postalCode]" 
                    value="${ address?.postalCode ?? '' }" 
                    autocomplete="postal-code" 
                    .required="${ true }"
                ></text-input>
                ${ (error) ? this.alertTemplate(error) : '' }
            </div>
        `;
    }

    countryInputTemplate(cartData: CartData, addressType: AddressType, address?: AddressData): TemplateResult {
        const error = this._errors[`${ addressType }Address.countryCode`];
        return html`
            <div class="Form-block" col-span="1">
                <label for="${ addressType }Address[countryCode]">
                    Country/Region
                </label>
                <select-input
                    name="${ addressType }Address[countryCode]"
                    value="${ address?.countryCode ?? '' }"
                    label="Select a country"
                    required="true"
                    .options="${ 
                        this._countryList.map((country) => {
                        return {
                            value: country.code,
                            label: country.name,
                            disabled: !country.available,
                        }
                    }) }"
                    @change=${ this._updateCountryCode }>
                </select-input>
                ${ (error) ? this.alertTemplate(error) : '' }
            </div>
        `;
    }

    stateInputTemplate(cartData: CartData, addressType: AddressType, address?: AddressData): TemplateResult {
        const countryCode = address?.countryCode || this._countryCode;
        const error = this._errors[`${ addressType }Address.administrativeArea`];
        const states = this._countryList.find((country) => country.code == countryCode)?.states ?? [];
        const options = states.map((state) => {
                return {
                    value: state.code,
                    label: state.name,
                }
            });
        return (countryCode)
            ? html`
                <div class="Form-block" col-span="1">
                    <label for="${ addressType }Address[administrativeArea]">
                        State/Province
                    </label>
                    <select-input
                        name="${ addressType }Address[administrativeArea]"
                        value="${ address?.administrativeArea ?? '' }"
                        ?required="${true}"
                        label="Select a state/province"
                        .options="${ options }">
                    </select-input>
                    ${ (error) ? this.alertTemplate(error) : '' }
                </div>
            `
            : html``;
    }

    // Methods

    private _updateCountryCode(e: Event) {
        const input = e.target as SelectInput;
        this._countryCode = input.value;
        this.requestUpdate();
    }

}